import moment from 'moment';
import { ModelBase } from '../abstract/model-base.model';
import { Currency } from '../core/currency.model';
import { InvoiceNumbering } from './invoice-numbering.model';
import { PaymentMethod } from '../core/payment-method.model';
import { PaymentTerm } from '../invoicing/payment-term.model';
import { PersonNameOrder } from '../core/person-name-order.model';
import { PricingMode } from '../core/pricing-mode.model';
import { SalesDocumentTemplate } from './template.model';
import { FkField } from '../../decorators/api-field.decorator';

export class SalesSettings extends ModelBase {

    static override CLASS_NAME: string = 'SalesSettings';

    private _addressNameOrder!: PersonNameOrder;
    private _bankDetails!: string;
    private _chequeLabel!: string;
    private _creditNoteEmailBody!: string;
    private _creditNoteEmailSubject!: string;
    private _creditNoteExists!: boolean;
    private _creditNoteFirstNumber!: number;
    private _creditNoteGeneralConditions!: string;
    private _creditNoteNumbering!: InvoiceNumbering;
    private _creditNoteNumberLength!: number;
    private _creditNoteNumberMonthSeparator!: string;
    private _creditNoteNumberPrefix!: string;
    private _creditNoteNumberPrefixSeparator!: string;
    private _creditNoteNumberYearSeparator!: string;
    private _creditNoteNumberShowMonth!: boolean;
    private _creditNoteNumberShowYear!: boolean;
    private _creditNoteDedicatedNumbering!: boolean;
    private _creditNoteTemplate!: SalesDocumentTemplate | null;
    private _currency!: Currency | null;
    private _invoiceAutoValidation!: boolean;
    private _invoiceEmailBody!: string;
    private _invoiceEmailSubject!: string;
    private _invoiceExists!: boolean;
    private _invoiceFirstNumber!: number;
    private _invoiceGeneralConditions!: string;
    private _invoiceNumbering!: InvoiceNumbering;
    private _invoiceNumberLength!: number;
    private _invoiceNumberMonthSeparator!: string;
    private _invoiceNumberPrefix!: string;
    private _invoiceNumberPrefixSeparator!: string;
    private _invoiceNumberYearSeparator!: string;
    private _invoiceNumberShowMonth!: boolean;
    private _invoiceNumberShowYear!: boolean;
    private _invoicePaymentTerm!: PaymentTerm;
    private _invoiceReminderEmailBody!: string;
    private _invoiceReminderEmailSubject!: string;
    private _invoiceTemplate!: SalesDocumentTemplate | null;
    private _invoicingDateFrom!: moment.Moment;
    private _partnerNameOrder!: PersonNameOrder;
    private _paymentMethods!: PaymentMethod[];
    private _pricingMode!: PricingMode;
    private _quotationAcceptanceConditions!: string;
    private _quotationAddendumFastForward!: boolean;
    private _quotationEmailBody!: string;
    private _quotationEmailSubject!: string;
    private _quotationExists!: boolean;
    private _quotationFirstNumber!: number;
    private _quotationNumbering!: InvoiceNumbering;
    private _quotationNumberLength!: number;
    private _quotationNumberMonthSeparator!: string;
    private _quotationNumberPrefix!: string;
    private _quotationNumberPrefixSeparator!: string;
    private _quotationNumberYearSeparator!: string;
    private _quotationNumberShowMonth!: boolean;
    private _quotationNumberShowYear!: boolean;
    private _quotationPaymentSchedule!: boolean;
    private _quotationReminderEmailBody!: string;
    private _quotationReminderEmailSubject!: string;
    private _quotationTemplate!: SalesDocumentTemplate | null;
    private _quotationValidityPeriod!: number;

    /**
     * Ordre du nom et du prénom dans le bloc adresse d'un particulier sur les documents de
     * vente — aperçu à l'écran et PDF (#1088). Servi en `{code, label}`, défaut `first_last`.
     */
    @FkField('code')
    public get addressNameOrder(): PersonNameOrder {
        return this._addressNameOrder;
    }

    public set addressNameOrder(value: any) {
        this._setValue('_addressNameOrder', value, 'PersonNameOrder');
    }

    public get bankDetails(): string {
        return this._bankDetails;
    }

    public set bankDetails(value: any) {
        this._setValue('_bankDetails', value, 'string');
    }

    public get chequeLabel(): string {
        return this._chequeLabel;
    }

    public set chequeLabel(value: any) {
        this._setValue('_chequeLabel', value, 'string');
    }

    public get creditNoteEmailBody(): string {
        return this._creditNoteEmailBody;
    }

    public set creditNoteEmailBody(value: any) {
        this._setValue('_creditNoteEmailBody', value, 'string');
    }

    public get creditNoteEmailSubject(): string {
        return this._creditNoteEmailSubject;
    }

    public set creditNoteEmailSubject(value: any) {
        this._setValue('_creditNoteEmailSubject', value, 'string');
    }

    public get creditNoteExists(): boolean {
        return this._creditNoteExists;
    }

    public set creditNoteExists(value: any) {
        this._setValue('_creditNoteExists', value, 'boolean');
    }

    public get creditNoteFirstNumber(): number {
        return this._creditNoteFirstNumber;
    }

    public set creditNoteFirstNumber(value: any) {
        this._setValue('_creditNoteFirstNumber', value, 'integer');
    }

    public get creditNoteGeneralConditions(): string {
        return this._creditNoteGeneralConditions;
    }

    public set creditNoteGeneralConditions(value: any) {
        this._setValue('_creditNoteGeneralConditions', value, 'string');
    }

    public get creditNoteNumbering(): InvoiceNumbering {
        return this._creditNoteNumbering;
    }

    public set creditNoteNumbering(value: any) {
        this._setValue('_creditNoteNumbering', value, 'InvoiceNumbering');
    }

    public get creditNoteNumberLength(): number {
        return this._creditNoteNumberLength;
    }

    public set creditNoteNumberLength(value: any) {
        this._setValue('_creditNoteNumberLength', value, 'integer');
    }

    public get creditNoteNumberPrefix(): string {
        return this._creditNoteNumberPrefix;
    }

    public set creditNoteNumberPrefix(value: any) {
        this._setValue('_creditNoteNumberPrefix', value, 'string');
    }

    public get creditNoteNumberPrefixSeparator(): string {
        return this._creditNoteNumberPrefixSeparator;
    }

    public set creditNoteNumberPrefixSeparator(value: any) {
        this._setValue('_creditNoteNumberPrefixSeparator', value, 'string');
    }

    public get creditNoteNumberYearSeparator(): string {
        return this._creditNoteNumberYearSeparator;
    }

    public set creditNoteNumberYearSeparator(value: any) {
        this._setValue('_creditNoteNumberYearSeparator', value, 'string');
    }

    public get creditNoteNumberMonthSeparator(): string {
        return this._creditNoteNumberMonthSeparator;
    }

    public set creditNoteNumberMonthSeparator(value: any) {
        this._setValue('_creditNoteNumberMonthSeparator', value, 'string');
    }

    public get creditNoteNumberShowMonth(): boolean {
        return this._creditNoteNumberShowMonth;
    }

    public set creditNoteNumberShowMonth(value: any) {
        this._setValue('_creditNoteNumberShowMonth', value, 'boolean');
    }

    public get creditNoteNumberShowYear(): boolean {
        return this._creditNoteNumberShowYear;
    }

    public set creditNoteNumberShowYear(value: any) {
        this._setValue('_creditNoteNumberShowYear', value, 'boolean');
    }

    public get creditNoteDedicatedNumbering(): boolean {
        return this._creditNoteDedicatedNumbering;
    }

    public set creditNoteDedicatedNumbering(value: any) {
        this._setValue('_creditNoteDedicatedNumbering', value, 'boolean');
    }

    /**
     * Gabarit par défaut des avoirs du dossier (#1114). Jamais vide une fois le dossier
     * initialisé ; `null` envoyé = le gabarit en place est gardé.
     */
    @FkField('id')
    public get creditNoteTemplate(): SalesDocumentTemplate | null {
        return this._creditNoteTemplate;
    }

    public set creditNoteTemplate(value: any) {
        this._setValue('_creditNoteTemplate', value, 'SalesDocumentTemplate');
    }

    /**
     * Devise des nouveaux documents du dossier (#1114), initialisée à celle du pays.
     * `null` envoyé = retour à la devise du pays, jamais vide en lecture.
     */
    @FkField('code')
    public get currency(): Currency | null {
        return this._currency;
    }

    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    public get invoiceAutoValidation(): boolean {
        return this._invoiceAutoValidation;
    }

    public set invoiceAutoValidation(value: any) {
        this._setValue('_invoiceAutoValidation', value, 'boolean');
    }

    public get invoiceEmailBody(): string {
        return this._invoiceEmailBody;
    }

    public set invoiceEmailBody(value: any) {
        this._setValue('_invoiceEmailBody', value, 'string');
    }

    public get invoiceEmailSubject(): string {
        return this._invoiceEmailSubject;
    }

    public set invoiceEmailSubject(value: any) {
        this._setValue('_invoiceEmailSubject', value, 'string');
    }

    public get invoiceExists(): boolean {
        return this._invoiceExists;
    }

    public set invoiceExists(value: any) {
        this._setValue('_invoiceExists', value, 'boolean');
    }

    public get invoiceFirstNumber(): number {
        return this._invoiceFirstNumber;
    }

    public set invoiceFirstNumber(value: any) {
        this._setValue('_invoiceFirstNumber', value, 'integer');
    }

    public get invoiceGeneralConditions(): string {
        return this._invoiceGeneralConditions;
    }

    public set invoiceGeneralConditions(value: any) {
        this._setValue('_invoiceGeneralConditions', value, 'string');
    }

    public get invoiceNumbering(): InvoiceNumbering {
        return this._invoiceNumbering;
    }

    public set invoiceNumbering(value: any) {
        this._setValue('_invoiceNumbering', value, 'InvoiceNumbering');
    }

    public get invoiceNumberLength(): number {
        return this._invoiceNumberLength;
    }

    public set invoiceNumberLength(value: any) {
        this._setValue('_invoiceNumberLength', value, 'integer');
    }

    public get invoiceNumberPrefix(): string {
        return this._invoiceNumberPrefix;
    }

    public set invoiceNumberPrefix(value: any) {
        this._setValue('_invoiceNumberPrefix', value, 'string');
    }

    public get invoiceNumberPrefixSeparator(): string {
        return this._invoiceNumberPrefixSeparator;
    }

    public set invoiceNumberPrefixSeparator(value: any) {
        this._setValue('_invoiceNumberPrefixSeparator', value, 'string');
    }

    public get invoiceNumberYearSeparator(): string {
        return this._invoiceNumberYearSeparator;
    }

    public set invoiceNumberYearSeparator(value: any) {
        this._setValue('_invoiceNumberYearSeparator', value, 'string');
    }

    public get invoiceNumberMonthSeparator(): string {
        return this._invoiceNumberMonthSeparator;
    }

    public set invoiceNumberMonthSeparator(value: any) {
        this._setValue('_invoiceNumberMonthSeparator', value, 'string');
    }

    public get invoiceNumberShowMonth(): boolean {
        return this._invoiceNumberShowMonth;
    }

    public set invoiceNumberShowMonth(value: any) {
        this._setValue('_invoiceNumberShowMonth', value, 'boolean');
    }

    public get invoiceNumberShowYear(): boolean {
        return this._invoiceNumberShowYear;
    }

    public set invoiceNumberShowYear(value: any) {
        this._setValue('_invoiceNumberShowYear', value, 'boolean');
    }

    public get invoicePaymentTerm(): PaymentTerm {
        return this._invoicePaymentTerm;
    }

    public set invoicePaymentTerm(value: any) {
        this._setValue('_invoicePaymentTerm', value, 'PaymentTerm');
    }

    public get invoiceReminderEmailBody(): string {
        return this._invoiceReminderEmailBody;
    }

    public set invoiceReminderEmailBody(value: any) {
        this._setValue('_invoiceReminderEmailBody', value, 'string');
    }

    public get invoiceReminderEmailSubject(): string {
        return this._invoiceReminderEmailSubject;
    }

    public set invoiceReminderEmailSubject(value: any) {
        this._setValue('_invoiceReminderEmailSubject', value, 'string');
    }

    /** Gabarit par défaut des factures du dossier (#1114) — mêmes règles que l'avoir. */
    @FkField('id')
    public get invoiceTemplate(): SalesDocumentTemplate | null {
        return this._invoiceTemplate;
    }

    public set invoiceTemplate(value: any) {
        this._setValue('_invoiceTemplate', value, 'SalesDocumentTemplate');
    }

    public get invoicingDateFrom(): moment.Moment {
        return this._invoicingDateFrom;
    }

    public set invoicingDateFrom(value: any) {
        this._setValue('_invoicingDateFrom', value, 'date');
    }

    /**
     * Ordre du nom et du prénom dans la dénomination composée à la création d'un tiers
     * particulier (#1087). Servi en `{code, label}`, défaut `last_first`.
     */
    @FkField('code')
    public get partnerNameOrder(): PersonNameOrder {
        return this._partnerNameOrder;
    }

    public set partnerNameOrder(value: any) {
        this._setValue('_partnerNameOrder', value, 'PersonNameOrder');
    }

    public get paymentMethods(): PaymentMethod[] {
        return this._paymentMethods;
    }

    public set paymentMethods(value: any) {
        this._setValue('_paymentMethods', value, 'PaymentMethod');
    }

    @FkField('code')
    public get pricingMode(): PricingMode {
        return this._pricingMode;
    }

    public set pricingMode(value: any) {
        this._setValue('_pricingMode', value, 'PricingMode');
    }

    public get quotationAcceptanceConditions(): string {
        return this._quotationAcceptanceConditions;
    }

    public set quotationAcceptanceConditions(value: any) {
        this._setValue('_quotationAcceptanceConditions', value, 'string');
    }

    /**
     * Défaut d'entreprise pour l'acceptation automatique des avenants (#1001) :
     * premier niveau de la cascade, surchargé par la fiche client puis figé sur
     * chaque avenant à sa création.
     */
    public get quotationAddendumFastForward(): boolean {
        return this._quotationAddendumFastForward;
    }

    public set quotationAddendumFastForward(value: any) {
        this._setValue('_quotationAddendumFastForward', value, 'boolean');
    }

    public get quotationEmailBody(): string {
        return this._quotationEmailBody;
    }

    public set quotationEmailBody(value: any) {
        this._setValue('_quotationEmailBody', value, 'string');
    }

    public get quotationEmailSubject(): string {
        return this._quotationEmailSubject;
    }

    public set quotationEmailSubject(value: any) {
        this._setValue('_quotationEmailSubject', value, 'string');
    }

    public get quotationExists(): boolean {
        return this._quotationExists;
    }

    public set quotationExists(value: any) {
        this._setValue('_quotationExists', value, 'boolean');
    }

    public get quotationFirstNumber(): number {
        return this._quotationFirstNumber;
    }

    public set quotationFirstNumber(value: any) {
        this._setValue('_quotationFirstNumber', value, 'integer');
    }

    public get quotationNumbering(): InvoiceNumbering {
        return this._quotationNumbering;
    }

    public set quotationNumbering(value: any) {
        this._setValue('_quotationNumbering', value, 'InvoiceNumbering');
    }

    public get quotationNumberLength(): number {
        return this._quotationNumberLength;
    }

    public set quotationNumberLength(value: any) {
        this._setValue('_quotationNumberLength', value, 'integer');
    }

    public get quotationNumberPrefix(): string {
        return this._quotationNumberPrefix;
    }

    public set quotationNumberPrefix(value: any) {
        this._setValue('_quotationNumberPrefix', value, 'string');
    }

    public get quotationNumberPrefixSeparator(): string {
        return this._quotationNumberPrefixSeparator;
    }

    public set quotationNumberPrefixSeparator(value: any) {
        this._setValue('_quotationNumberPrefixSeparator', value, 'string');
    }

    public get quotationNumberYearSeparator(): string {
        return this._quotationNumberYearSeparator;
    }

    public set quotationNumberYearSeparator(value: any) {
        this._setValue('_quotationNumberYearSeparator', value, 'string');
    }

    public get quotationNumberMonthSeparator(): string {
        return this._quotationNumberMonthSeparator;
    }

    public set quotationNumberMonthSeparator(value: any) {
        this._setValue('_quotationNumberMonthSeparator', value, 'string');
    }

    public get quotationNumberShowMonth(): boolean {
        return this._quotationNumberShowMonth;
    }

    public set quotationNumberShowMonth(value: any) {
        this._setValue('_quotationNumberShowMonth', value, 'boolean');
    }

    public get quotationNumberShowYear(): boolean {
        return this._quotationNumberShowYear;
    }

    public set quotationNumberShowYear(value: any) {
        this._setValue('_quotationNumberShowYear', value, 'boolean');
    }

    public get quotationPaymentSchedule(): boolean {
        return this._quotationPaymentSchedule;
    }

    public set quotationPaymentSchedule(value: any) {
        this._setValue('_quotationPaymentSchedule', value, 'boolean');
    }

    public get quotationReminderEmailBody(): string {
        return this._quotationReminderEmailBody;
    }

    public set quotationReminderEmailBody(value: any) {
        this._setValue('_quotationReminderEmailBody', value, 'string');
    }

    public get quotationReminderEmailSubject(): string {
        return this._quotationReminderEmailSubject;
    }

    public set quotationReminderEmailSubject(value: any) {
        this._setValue('_quotationReminderEmailSubject', value, 'string');
    }

    /** Gabarit par défaut des devis du dossier (#1114) — mêmes règles que l'avoir. */
    @FkField('id')
    public get quotationTemplate(): SalesDocumentTemplate | null {
        return this._quotationTemplate;
    }

    public set quotationTemplate(value: any) {
        this._setValue('_quotationTemplate', value, 'SalesDocumentTemplate');
    }

    public get quotationValidityPeriod(): number {
        return this._quotationValidityPeriod;
    }


    public set quotationValidityPeriod(value: any) {
        this._setValue('_quotationValidityPeriod', value, 'integer');
    }

}
